import React, { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { OneWayInOpportunitySimpleV2Dto, OneWayInStatus } from 'src/api/v2';
import { Button, Icon } from '@innovationnorway/cl-react';
import { hoverEnter, hoverLeave } from '../helpers';
import OpportunityCard from './OpportunityCard';
import OpportunityCardSkeleton from './OpportunityCardSkeleton';

const SKELETON_CARD_COUNT = 3;

// Starting guess only; every row is measured for real once rendered.
const ESTIMATED_ROW_HEIGHT = 334;

const OpportunityColumn = ({
  opportunityList,
  status,
  minimizable = false,
  name,
  onChangeStatus,
  color,
  statusLegend,
  getMunicipalityName,
  advisorNameById,
  advisorInitialsById,
  advisorColorClassById,
  advisorsLoading,
  onRecommendServicesModalOpen,
  onDeliverServicesModalOpen,
  isLoading = false,
}: {
  opportunityList: OneWayInOpportunitySimpleV2Dto[];
  status?: OneWayInStatus;
  minimizable?: boolean;
  name: string;
  onChangeStatus: (id: string, status: OneWayInStatus) => void;
  color: 'green' | 'orange' | 'gray' | 'blue';
  statusLegend?: ReactNode;
  getMunicipalityName: (number: string | undefined) => string | null | undefined;
  advisorNameById: Map<string, string>;
  advisorInitialsById: Map<string, string>;
  advisorColorClassById: Map<string, string>;
  advisorsLoading: boolean;
  onRecommendServicesModalOpen: (opportunity: OneWayInOpportunitySimpleV2Dto) => void;
  onDeliverServicesModalOpen: (opportunity: OneWayInOpportunitySimpleV2Dto) => void;
  isLoading?: boolean;
}) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const [minimized, setMinimized] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: isLoading ? SKELETON_CARD_COUNT : opportunityList.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => (isLoading ? `skeleton-${index}` : opportunityList[index].id),
    // Generous, so a card being dragged isn't unmounted when it scrolls just out of view.
    overscan: 8,
  });

  return minimized ? (
    <button
      className="clickable-wrapper display--flex padding-top--2 flex-direction--column"
      onClick={() => setMinimized(false)}
    >
      <Icon className="" iconName="angle-double-left" prefix="fasr" />
      <div className="minimized margin-top--2">{name}</div>
    </button>
  ) : (
    <section
      onDrop={(e) => {
        e.preventDefault();
        if (status) {
          const data = e.dataTransfer.getData('text/plain');
          onChangeStatus(data, status);
          hoverLeave(e);
        }
      }}
      onDragEnter={(e) => {
        hoverEnter(e);
      }}
      onDragLeave={(e) => {
        hoverLeave(e);
      }}
      onDragOver={(e) => {
        hoverEnter(e);
        e.preventDefault();
      }}
      className={`stack board__column  ${!status && 'inactive'}`}
    >
      <div className="flex justify-content--space-between">
        {statusLegend ? (
          statusLegend
        ) : (
          <div className="flex gap--1 col__name">
            <div className={`dot ${color}`}></div>
            <div className={``}>{name}</div>
          </div>
        )}
        {minimizable && (
          <Button
            type="button"
            icon
            variant="tertiary"
            className="mini__button"
            aria-label={tOneWayIn('board.minimizeColumn')}
            onClick={() => setMinimized(true)}
          >
            <Icon prefix="fasr" iconName="angle-double-right" />
          </Button>
        )}
      </div>
      <div className="scrollable" ref={scrollRef}>
        <div className="board__rows" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const opportunity = opportunityList[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="board__row"
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                {isLoading ? (
                  <OpportunityCardSkeleton />
                ) : (
                  <OpportunityCard
                    opportunity={opportunity}
                    municipalityName={getMunicipalityName(opportunity.municipality)}
                    advisorName={
                      opportunity.assignedTo ? advisorNameById.get(opportunity.assignedTo) : undefined
                    }
                    advisorInitials={
                      opportunity.assignedTo ? advisorInitialsById.get(opportunity.assignedTo) : undefined
                    }
                    advisorColorClass={
                      (opportunity.assignedTo && advisorColorClassById.get(opportunity.assignedTo)) || ''
                    }
                    advisorsLoading={advisorsLoading}
                    onRecommendServicesModalOpen={onRecommendServicesModalOpen}
                    onDeliverServicesModalOpen={onDeliverServicesModalOpen}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* <div className="scrollable stack">
        {isLoading
          ? Array.from({ length: skeletonCardCount }).map((_, index) => (
              <OpportunityCardSkeleton key={index} />
            ))
          : opportunityList.map((oppo) => (
              <OpportunityCard
                key={oppo.id}
                opportunity={oppo}
                onRecommendServicesModalOpen={onRecommendServicesModalOpen}
                onDeliverServicesModalOpen={onDeliverServicesModalOpen}
              />
            ))}
      </div> */}
    </section>
  );
};
export default OpportunityColumn;
